import { useEffect, useId, useState } from 'react';

import {
  EXEC_GRANT_MAX_TTL_MINUTES,
  executionGrantMaximumLabel,
  executionGrantExpiryPreview,
  parseExecutionGrantDuration,
  type ExecutionGrantDurationUnit
} from '../utils/executionGrantDuration';
import './ExecutionGrantDurationField.css';

type Props = {
  amount: string;
  unit: ExecutionGrantDurationUnit;
  onAmountChange: (value: string) => void;
  onUnitChange: (value: ExecutionGrantDurationUnit) => void;
  disabled?: boolean;
  maxMinutes?: number;
  label?: string;
  baseTime?: Date;
  previewPrefix?: string;
};

export function ExecutionGrantDurationField({
  amount, unit, onAmountChange, onUnitChange, disabled = false, maxMinutes = EXEC_GRANT_MAX_TTL_MINUTES, label = 'Grant duration', baseTime, previewPrefix = 'If created now, expires'
}: Props) {
  const [now, setNow] = useState(() => new Date());
  const errorId = useId();
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const { minutes, error } = parseExecutionGrantDuration(amount, unit, maxMinutes);
  return (
    <div className="stack">
      <div role="group" aria-label={label}>
        <div className="status">{label}</div>
        <div className="execution-grant-duration__controls">
          <input
            type="number"
            min={0}
            step="any"
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
            aria-label={`${label} amount`}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            aria-errormessage={error ? errorId : undefined}
            disabled={disabled}
          />
          <select
            value={unit}
            onChange={(event) => onUnitChange(event.target.value as ExecutionGrantDurationUnit)}
            aria-label={`${label} unit`}
            disabled={disabled}
          >
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
          </select>
        </div>
      </div>
      <div className="status">Maximum: {executionGrantMaximumLabel(maxMinutes)}. Fractional hours or days must equal whole minutes.</div>
      {error ? (
        <div id={errorId} className="status error-inline" role="alert">{error}</div>
      ) : (
        <div className="status" aria-live="off">
          {previewPrefix} {executionGrantExpiryPreview(minutes!, new Date(Math.max(now.getTime(), baseTime?.getTime() ?? 0)))}.
        </div>
      )}
    </div>
  );
}
